import React, { useEffect, useMemo, useState } from "react";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { Sparkles, Check, Zap, Users, Rocket, HardDrive, Gift, ShieldCheck, Receipt, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

import { swallow } from "@/lib/log";
const PLAN_ORDER = ["free", "lite", "standard", "pro"];
const DEFAULT_TIERS = {
  free:     { name: "Free",     price_inr: 0,   storage_mb: 500,       billing: "free" },
  lite:     { name: "Lite",     price_inr: 99,  storage_mb: 5 * 1024,  billing: "yearly" },
  standard: { name: "Standard", price_inr: 299, storage_mb: 25 * 1024, billing: "yearly" },
  pro:      { name: "Pro",      price_inr: 799, storage_mb: 100 * 1024, billing: "yearly" },
};

const TIER_ICON = {
  free: Sparkles, lite: Zap, standard: Rocket, pro: Users,
};

const TIER_FEATURES = {
  free: [
    "AI Chat, Notes, Reminders",
    "500 MB total storage",
    "Password vault (up to 5)",
    "OCR scanner, Journal, Photos, Videos",
  ],
  lite: [
    "Everything in Free",
    "5 GB total storage",
    "Unlimited passwords",
    "Digital Legacy \u2014 nominees",
  ],
  standard: [
    "Everything in Lite",
    "25 GB total storage",
    "Family Vault (up to 6 members)",
    "Passkey unlock (WebAuthn)",
    "Priority AI answers",
  ],
  pro: [
    "Everything in Standard",
    "100 GB total storage",
    "Advanced sharing & backups",
    "Extended password history",
    "Early access to new features",
  ],
};

const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function fmtBytes(mb) {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Plan() {
  const [state, setState] = useState({ plan: "free", trial_active: false, trial_expires_at: null, tiers: null });
  const [usage, setUsage] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payingPlan, setPayingPlan] = useState(null);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const [p, u, meRes, hist] = await Promise.all([
        api.get("/plan"),
        api.get("/plan/usage"),
        api.get("/auth/me").catch(() => ({ data: null })),
        api.get("/payments/history").catch(() => ({ data: { items: [] } })),
      ]);
      setState(p.data);
      setUsage(u.data);
      if (meRes.data) setMe(meRes.data);
      setHistory(hist.data?.items || []);
    } catch (e) { swallow("Plan")(e); }
  };
  useEffect(() => { load(); }, []);

  const startTrial = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/plan/trial");
      await load();
      toast.success(`Free trial started \u2014 unlocks Standard until ${new Date(data.trial_expires_at).toLocaleDateString()}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const downgradeToFree = async () => {
    setBusy(true);
    try {
      await api.post("/plan", { plan: "free" });
      await load();
      toast.success("Switched to Free plan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to switch");
    } finally { setBusy(false); }
  };

  const payWithRazorpay = async (planId) => {
    if (planId === "free") return downgradeToFree();
    setBusy(true);
    setPayingPlan(planId);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error("Couldn't load Razorpay — check your internet and try again");
        return;
      }

      const { data: order } = await api.post("/payments/razorpay/order", { plan: planId });

      // Fetch the freshest user profile right before opening checkout so
      // Razorpay's prefill picks up phone/name updates without a page reload.
      let freshMe = me;
      try {
        const { data } = await api.get("/auth/me");
        freshMe = data;
        setMe(data);
      } catch (e) { swallow("Plan/refreshMe")(e); /* fall back to cached me */ }

      // Razorpay wants contact as an international-format string (e.g. +919876543210).
      // If the user stored just "9876543210", assume India and prepend +91.
      const normalizeContact = (raw) => {
        const v = String(raw || "").trim();
        if (!v) return "";
        if (v.startsWith("+")) return v.replace(/\s+/g, "");
        const digits = v.replace(/\D/g, "");
        if (digits.length === 10) return `+91${digits}`;
        if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
        return digits ? `+${digits}` : "";
      };
      const prefillContact = normalizeContact(freshMe?.phone);

      await new Promise((resolve) => {
        const options = {
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          name: "Impo",
          description: `${order.plan_name} plan \u2014 1 year`,
          order_id: order.order_id,
          prefill: {
            name: freshMe?.name || "",
            email: freshMe?.email || "",
            contact: prefillContact,
          },
          notes: { plan: planId },
          theme: { color: "#0F1F3D" },
          modal: {
            ondismiss: () => {
              toast.info("Payment cancelled");
              resolve();
            },
          },
          handler: async (resp) => {
            try {
              const { data } = await api.post("/payments/razorpay/verify", {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success(`Payment successful \u2014 you're on ${planId.charAt(0).toUpperCase()}${planId.slice(1)}!`);
              await load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Payment verification failed");
            } finally {
              resolve();
            }
          },
        };
        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", (evt) => {
          const err = evt?.error?.description || "Payment failed";
          toast.error(err);
          resolve();
        });
        rzp.open();
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
    } finally {
      setBusy(false);
      setPayingPlan(null);
    }
  };

  const tiers = useMemo(() => state.tiers || DEFAULT_TIERS, [state]);
  const order = PLAN_ORDER;
  const current = state.plan;
  const trialExpiresLabel = state.trial_expires_at ? new Date(state.trial_expires_at).toLocaleDateString() : null;
  const planExpiresLabel = state.plan_expires_at ? new Date(state.plan_expires_at).toLocaleDateString() : null;
  const trialUsed = usage && usage.trial_active === false && state.raw_plan !== "free";
  const paidHistory = history.filter((h) => h.status === "paid");

  return (
    <>
      <TopBar title="Plans & storage" subtitle="Simple, mostly-free, always private" back />
      <div className="px-4 py-4 space-y-4">
        {usage && (
          <div className="rounded-2xl border border-border bg-card p-4 impo-in" data-testid="storage-meter-card">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-primary" />
              <div className="font-display font-semibold text-sm">Storage</div>
              {state.trial_active && trialExpiresLabel && (
                <Badge className="ml-auto rounded-full text-[10px] bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]">Trial ends {trialExpiresLabel}</Badge>
              )}
              {!state.trial_active && planExpiresLabel && state.raw_plan !== "free" && (
                <Badge className="ml-auto rounded-full text-[10px]" variant="outline">Renews {planExpiresLabel}</Badge>
              )}
            </div>
            <div className="mt-3 flex items-baseline justify-between text-sm">
              <span className="font-mono">{fmtBytes(Math.max(usage.storage_used_mb, 0))} <span className="text-muted-foreground">of {fmtBytes(usage.storage_limit_mb)}</span></span>
              <span className="text-xs text-muted-foreground">{usage.usage_pct.toFixed(0)}%</span>
            </div>
            <div className="mt-2 h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full transition-[width] ${usage.usage_pct >= 90 ? "bg-destructive" : usage.usage_pct >= 70 ? "bg-[hsl(var(--warning))]" : "bg-primary"}`}
                style={{ width: `${Math.min(100, usage.usage_pct)}%` }}
                data-testid="storage-meter-fill"
              />
            </div>
            {usage.upgrade_suggestion && (
              <div className="mt-3 rounded-xl bg-[hsl(var(--accent))] p-3 text-xs text-accent-foreground flex items-start gap-2">
                <Rocket className="h-3.5 w-3.5 mt-0.5" />
                <span>You're using more than 80% of your space. Upgrade to <b className="capitalize">{usage.upgrade_suggestion.to}</b> ({fmtBytes(usage.upgrade_suggestion.storage_mb)}, ₹{usage.upgrade_suggestion.price_inr}/yr).</span>
              </div>
            )}
          </div>
        )}

        {!state.trial_active && !trialUsed && (
          <button onClick={startTrial} disabled={busy} className="w-full rounded-2xl border-2 border-dashed border-primary/40 bg-[hsl(var(--accent))] p-4 text-left hover:bg-primary/5 transition disabled:opacity-50" data-testid="plan-start-trial-button">
            <div className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              <div className="font-display font-semibold text-sm">Try Standard free for 30 days</div>
            </div>
            <div className="text-xs text-muted-foreground mt-1">25 GB storage + Family Vault + Passkey unlock. No card needed, no auto-renew.</div>
          </button>
        )}

        <div className="rounded-xl border border-border bg-card p-3 text-xs text-muted-foreground flex items-start gap-2">
          <ShieldCheck className="h-3.5 w-3.5 mt-0.5 text-primary" />
          <span><b>Impo is always free to use.</b> Free plan works forever — upgrade only if you want more storage or family sharing. Yearly only, no monthly gotchas.</span>
        </div>

        <div className="space-y-3">
          {order.map((id) => {
            const t = tiers[id];
            if (!t) return null;
            const Icon = TIER_ICON[id] || Sparkles;
            const isCurrent = current === id;
            const isRawPaid = state.raw_plan === id;
            const price = t.price_inr === 0 ? "Free forever" : `\u20b9${t.price_inr}/year`;
            const perDay = t.price_inr > 0 ? `about \u20b9${(t.price_inr / 365).toFixed(2)}/day` : null;
            const isPayingThis = payingPlan === id;
            return (
              <div key={id} className={`rounded-2xl border p-4 transition ${isCurrent ? "border-primary shadow-[0_10px_30px_rgba(15,23,42,0.08)]" : "border-border"} bg-card impo-in`} data-testid={`plan-card-${id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${isCurrent ? "bg-primary text-primary-foreground" : "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-display font-semibold">{t.name}</div>
                      <div className="text-xs text-muted-foreground">{price}{perDay ? ` \u00b7 ${perDay}` : ""}</div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {id === "lite" && <Badge className="rounded-full text-[10px]">Great value</Badge>}
                    {id === "standard" && <Badge className="rounded-full text-[10px] bg-primary text-primary-foreground">Most popular</Badge>}
                    {isCurrent && <Badge className="rounded-full text-[10px]" variant="outline">Current</Badge>}
                  </div>
                </div>
                <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                  <HardDrive className="h-3 w-3" /> {fmtBytes(t.storage_mb)} storage
                </div>
                <ul className="mt-3 space-y-1">
                  {(TIER_FEATURES[id] || []).map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-primary shrink-0" /> {f}</li>
                  ))}
                </ul>
                {!isRawPaid && id !== "free" && (
                  <Button
                    className="mt-3 w-full h-10"
                    onClick={() => payWithRazorpay(id)}
                    disabled={busy}
                    data-testid={`plan-choose-${id}`}
                  >
                    {isPayingThis ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Opening checkout…</>
                    ) : (
                      <>Choose {t.name} — ₹{t.price_inr}/year</>
                    )}
                  </Button>
                )}
                {isRawPaid && id !== "free" && (
                  <Button
                    variant="outline"
                    className="mt-3 w-full h-10"
                    onClick={downgradeToFree}
                    disabled={busy}
                    data-testid={`plan-downgrade-${id}`}
                  >
                    Cancel & switch to Free
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {paidHistory.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-4 impo-in" data-testid="payments-history-card">
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="h-4 w-4 text-primary" />
              <div className="font-display font-semibold text-sm">Payment history</div>
            </div>
            <ul className="space-y-2 text-sm">
              {paidHistory.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 py-1 border-b border-border/60 last:border-b-0" data-testid={`payment-row-${p.payment_id}`}>
                  <div>
                    <div className="capitalize font-medium">{p.plan} plan</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{p.payment_id}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono">₹{p.amount_inr}</div>
                    <div className="text-[11px] text-muted-foreground">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : ""}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-[11px] text-muted-foreground text-center pt-2 pb-4 flex items-center justify-center gap-1.5">
          <ShieldCheck className="h-3 w-3" />
          Secure payments powered by Razorpay
        </div>
      </div>
    </>
  );
}
